import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import {Link} from 'react-router-dom'
import {apiConstants, post} from '../../common/constants'
import {formatDate, isMerchantKycRejected} from '../../common/util'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import {PieChart} from '../../components/charts/PieChart'
import {BarChart, VerticalBarChart} from '../../components/charts/BarChart'


import CheckCircleOutlineIcon         from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon               from '@mui/icons-material/HighlightOff'
import HourglassEmptyIcon             from '@mui/icons-material/HourglassEmpty'
import PeopleOutlineIcon              from '@mui/icons-material/PeopleOutline'
import PendingActionsIcon             from '@mui/icons-material/PendingActions'
import SpeedIcon                      from '@mui/icons-material/Speed'
import AccessTimeIcon                 from '@mui/icons-material/AccessTime'
import WarningAmberIcon               from '@mui/icons-material/WarningAmber'
import TrendingDownIcon               from '@mui/icons-material/TrendingDown'
import InfoOutlinedIcon               from '@mui/icons-material/InfoOutlined'
import CloseIcon                      from '@mui/icons-material/Close'
import OpenInNewIcon                  from '@mui/icons-material/OpenInNew'

// ─── Professional colour palette ────────────────────────────────────────────
const C = {
  text:         '#0f172a',
  textSub:      '#475569',
  textMuted:    '#94a3b8',
  surface:      '#f8fafc',
  card:         '#ffffff',
  border:       '#e2e8f0',
  borderHover:  '#cbd5e1',
  divider:      '#f1f5f9',
  blue:         '#2563eb',
  blueDark:     '#1d4ed8',
  blueBg:       '#eff6ff',
  blueBorder:   '#bfdbfe',
  green:        '#15803d',
  greenBg:      '#f0fdf4',
  greenBorder:  '#bbf7d0',
  amber:        '#b45309',
  amberBg:      '#fffbeb',
  amberBorder:  '#fde68a',
  red:          '#b91c1c',
  redBg:        '#fef2f2',
  redBorder:    '#fecaca',
  purple:       '#6d28d9',
  purpleBg:     '#f5f3ff',
  purpleBorder: '#ddd6fe',
  sky:          '#0369a1',
  skyBg:        '#f0f9ff',
  skyBorder:    '#bae6fd',
}

// ─── KYC status → display meta ───────────────────────────────────────────────
const STATUS_META = {
  KYC_PENDING:  {label: 'Not Started',     color: C.amber, bg: C.amberBg, border: C.amberBorder},
  KYC_UPLOADED: {label: 'Awaiting Review', color: C.sky,   bg: C.skyBg,   border: C.skyBorder},
  KYC_APPROVED: {label: 'Approved',        color: C.green, bg: C.greenBg, border: C.greenBorder},
  KYC_REJECTED: {label: 'Rejected',        color: C.red,   bg: C.redBg,   border: C.redBorder},
}

// ─── Rich tooltip shown on every info icon ────────────────────────────────────
function MetricTooltip({formula, description, tip}) {
  return (
    <Box sx={{p: 1.25, maxWidth: 260}}>
      {formula && (
        <Box
          sx={{
            fontFamily: '"Roboto Mono", monospace',
            fontSize: '10.5px',
            backgroundColor: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: '6px',
            px: 1.25,
            py: 0.75,
            mb: 1,
            color: '#93c5fd',
            letterSpacing: '0.01em',
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          }}
        >
          {formula}
        </Box>
      )}
      <Typography sx={{fontSize: '11.5px', color: '#e2e8f0', lineHeight: 1.6}}>
        {description}
      </Typography>
      {tip && (
        <>
          <Divider sx={{my: 0.75, borderColor: 'rgba(255,255,255,0.12)'}} />
          <Typography sx={{fontSize: '10.5px', color: '#94a3b8', lineHeight: 1.5, fontStyle: 'italic'}}>
            💡 {tip}
          </Typography>
        </>
      )}
    </Box>
  )
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({icon, label, value, sub, color, bg, border, highlight, tooltipProps, onClick}) {
  const clickable = Boolean(onClick)
  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: '14px',
        border: `1px solid ${highlight ? border || color + '60' : C.border}`,
        backgroundColor: C.card,
        p: '18px 20px 16px',
        position: 'relative',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      {/* Info icon – top right; stop-propagation so it doesn't open the drawer */}
      {tooltipProps && (
        <Tooltip
          title={<MetricTooltip {...tooltipProps} />}
          placement='top'
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#1e293b',
                '& .MuiTooltip-arrow': {color: '#1e293b'},
                boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
                borderRadius: '10px',
                p: 0,
              },
            },
          }}
        >
          <IconButton
            size='small'
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              p: 0.4,
              color: C.textMuted,
              '&:hover': {color: C.textSub, backgroundColor: C.divider},
              borderRadius: '6px',
            }}
          >
            <InfoOutlinedIcon sx={{fontSize: 14}} />
          </IconButton>
        </Tooltip>
      )}

      {/* Icon badge */}
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '10px',
          backgroundColor: bg,
          border: `1px solid ${border || color + '30'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        {React.cloneElement(icon, {sx: {fontSize: 19, color}})}
      </Box>

      {/* Value */}
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '1.9rem',
          color: highlight ? color : C.text,
          lineHeight: 1,
          mb: 0.5,
          letterSpacing: '-0.02em',
        }}
      >
        {value ?? '—'}
      </Typography>

      {/* Label */}
      <Typography
        sx={{
          fontSize: '0.67rem',
          fontWeight: 700,
          color: C.textSub,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          mb: 0.3,
        }}
      >
        {label}
      </Typography>

      {/* Sub */}
      {sub && (
        <Typography sx={{fontSize: '0.7rem', color: C.textMuted, lineHeight: 1.4}}>
          {sub}
        </Typography>
      )}

      {/* "Click to view" hint */}
      {clickable && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.4,
            mt: 1,
            opacity: 0.5,
          }}
        >
          <OpenInNewIcon sx={{fontSize: 10, color: C.textMuted}} />
          <Typography sx={{fontSize: '0.62rem', color: C.textMuted, letterSpacing: '0.03em'}}>
            Click to view details
          </Typography>
        </Box>
      )}
    </Box>
  )
}

// ─── Section card wrapper ─────────────────────────────────────────────────────
function SectionCard({title, badge, right, noPadBody, children}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${C.border}`,
        overflow: 'hidden',
        backgroundColor: C.card,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.surface,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Typography sx={{fontWeight: 700, fontSize: '0.875rem', color: C.text}}>
            {title}
          </Typography>
          {badge != null && (
            <Box
              sx={{
                px: 1,
                py: 0.15,
                borderRadius: '5px',
                fontSize: '0.62rem',
                fontWeight: 700,
                backgroundColor: C.blueBg,
                color: C.blue,
                border: `1px solid ${C.blueBorder}`,
              }}
            >
              {badge}
            </Box>
          )}
        </Box>
        {right}
      </Box>
      <Box sx={noPadBody ? {} : {p: 2.5}}>{children}</Box>
    </Paper>
  )
}

// ─── Segmented pipeline funnel bar ───────────────────────────────────────────
function FunnelBar({segments}) {
  const total = segments.reduce((s, x) => s + x.value, 0)
  if (!total) return null
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          height: 32,
          borderRadius: '10px',
          overflow: 'hidden',
          mb: 1.75,
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {segments.map((seg, i) => {
          const pct = (seg.value / total) * 100
          if (!pct) return null
          return (
            <Tooltip key={i} title={`${seg.label}: ${seg.value} (${pct.toFixed(1)}%)`} arrow>
              <Box
                sx={{
                  width: `${pct}%`,
                  backgroundColor: seg.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: pct > 4 ? undefined : 3,
                  transition: 'width 0.45s cubic-bezier(0.4,0,0.2,1)',
                  cursor: 'default',
                  borderRight: i < segments.length - 1 ? '1.5px solid rgba(255,255,255,0.35)' : 'none',
                }}
              >
                {pct > 9 && (
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: '#fff',
                      userSelect: 'none',
                      textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    }}
                  >
                    {pct.toFixed(0)}%
                  </Typography>
                )}
              </Box>
            </Tooltip>
          )
        })}
      </Box>
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2.5}}>
        {segments.map((seg, i) => {
          const pct = ((seg.value / total) * 100).toFixed(1)
          return (
            <Box key={i} sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '3px',
                  backgroundColor: seg.color,
                  flexShrink: 0,
                  boxShadow: `0 1px 3px ${seg.color}60`,
                }}
              />
              <Typography sx={{fontSize: '0.73rem', color: C.textSub, fontWeight: 500}}>
                {seg.label}
              </Typography>
              <Typography sx={{fontSize: '0.73rem', color: C.text, fontWeight: 700}}>
                {seg.value}
              </Typography>
              <Typography sx={{fontSize: '0.68rem', color: C.textMuted}}>({pct}%)</Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

// ─── Tooltip definitions ─────────────────────────────────────────────────────
const TIPS = {
  totalMerchants: {
    description:
      'Total count of active, non-test merchant accounts registered on Proship. Excludes soft-deleted and sandbox accounts.',
    tip: 'This is your total addressable merchant base on the platform.',
  },
  kycNotStarted: {
    description:
      'Merchants who completed registration but have not uploaded any KYC documents yet. These are drop-offs in the onboarding funnel.',
    tip: 'High numbers here indicate an onboarding friction point. Consider automated nudges.',
  },
  awaitingReview: {
    description:
      'Merchants who have uploaded all required KYC documents and are waiting for the admin team to review and approve them.',
    tip: 'Each record here needs a team action. Monitor this to manage your review SLA.',
  },
  approvedLive: {
    description:
      'Merchants who have passed full KYC verification. They can process shipments, receive COD remittance, and use all platform features.',
    tip: 'This is your active, revenue-generating merchant base.',
  },
  docsRejected: {
    description:
      'Merchants who have at least one KYC document rejected by the review team. The merchant must re-upload the rejected document(s).',
    tip: 'Follow up with these merchants promptly to unblock their onboarding.',
  },
  approvalRate: {
    formula: 'Approved Merchants\n─────────────────────────── × 100\n(Approved + Rejected) Merchants',
    description:
      'The percentage of merchants who submitted KYC documents and were ultimately approved. Measures conversion quality of submitted applications.',
    tip: 'A rate below 80% may indicate unclear guidelines or poor document quality from merchants.',
  },
  avgDays: {
    formula: 'Mean of:\n  latest(verifiedDate) − earliest(uploadDate)\nacross all KYC-approved merchants',
    description:
      "Average calendar days from when a merchant uploads their first document to when their final document is approved. Measures the review team's throughput.",
    tip: 'Lower is better. Industry benchmark for KYC review is 1–3 business days.',
  },
  overdue: {
    formula: 'Merchants in "Awaiting Review"\nwhere today − earliest(uploadDate) > 7 days',
    description:
      'Applications that have been waiting for admin review for more than 7 days, measured from the date of first document upload.',
    tip: 'These are the highest-risk items. Delayed approvals directly delay merchant activation and revenue.',
  },
  docRejectionRate: {
    formula: 'Rejected Documents\n──────────────────────── × 100\nAll Reviewed Documents',
    description:
      'The percentage of individual KYC documents that were rejected during review, out of all documents that have been actioned (approved or rejected).',
    tip: 'A high rate may indicate that merchants are submitting blurry scans, wrong file types, or mismatched documents.',
  },
}

// ─── Shared renderCell helpers ────────────────────────────────────────────────
function MerchantNameCell({row}) {
  return (
    <Link
      to={`/merchant-config/merchant/${row.id}`}
      style={{color: C.blue, textDecoration: 'none', fontWeight: 600}}
    >
      {row.companyName || row.name}
    </Link>
  )
}

function StatusChip({status}) {
  const meta = STATUS_META[status] || {
    label: status || '—',
    color: C.textSub,
    bg: C.divider,
    border: C.border,
  }
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.3,
        borderRadius: '6px',
        backgroundColor: meta.bg,
        border: `1px solid ${meta.border}`,
        display: 'inline-flex',
      }}
    >
      <Typography sx={{fontSize: '10.5px', fontWeight: 700, color: meta.color}}>
        {meta.label}
      </Typography>
    </Box>
  )
}


// ─── Main component ───────────────────────────────────────────────────────────
export default function KYCAnalytics() {
  const [rawData, setRawData]             = useState([])
  const [loading, setLoading]             = useState(false)

  // Approver table pagination
  const [approverPage, setApproverPage] = useState(0)
  const [approverSize, setApproverSize] = useState(10)

  // Drawer state
  const [drawerState, setDrawerState] = useState({
    open: false, title: '', subtitle: '', rows: [], columns: [], getRowId: (r) => r.id, autoRowHeight: false,
  })
  const [drawerPage, setDrawerPage]   = useState(0)
  const [drawerSize, setDrawerSize]   = useState(25)

  // Strip sorting from every column before storing in drawer state
  const openDrawer = useCallback(({title, subtitle, rows, columns, getRowId, autoRowHeight = false}) => {
    setDrawerState({
      open: true,
      title,
      subtitle: subtitle || '',
      rows: rows || [],
      columns: columns.map((c) => ({...c, sortable: false})),
      getRowId: getRowId || ((r) => r.id),
      autoRowHeight,
    })
    setDrawerPage(0)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerState((prev) => ({...prev, open: false}))
  }, [])

  // ── Data fetch ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(() => {
    setLoading(true)
    post(apiConstants.MERCHANT_SEARCH_CACHED, {
      offset: 0,
      limit: 10000,
      andfilter: [],
      includes:
        'name,status,merchantId,id,address,city,state,companyName,is_deleted,test_merchant,kycDetails,createdDate',
    })
      .then((res) => {
        setRawData(res.data?.data || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // ── All metrics + enriched lists ────────────────────────────────────────────
  const M = useMemo(() => {
    const active = rawData.filter((v) => v.is_deleted === '0' && !v.test_merchant)
    if (!active.length) return null

    const now          = Date.now()
    const OVERDUE_DAYS = 7

    const total           = active.length
    const kycPendingList  = active.filter((m) => m.status === 'KYC_PENDING')
    const kycUploadedList = active.filter((m) => m.status === 'KYC_UPLOADED')
    const kycApprovedList = active.filter((m) => m.status === 'KYC_APPROVED')
    const kycRejectedList = active.filter(isMerchantKycRejected)

    // ── Document-level arrays ──
    // Normalise: rejected docs may carry rejectedBy/rejectedDate instead of verifiedBy/verifiedDate
    const allDocs = active.flatMap((m) =>
      (m.kycDetails || []).map((d) => ({
        ...d,
        verifiedBy:   d.verifiedBy   || d.rejectedBy   || null,
        verifiedDate: d.verifiedDate || d.rejectedDate  || null,
        merchantOid:  m.id,
        merchantId:   m.merchantId,
        merchantName: m.companyName || m.name,
      }))
    )
    const approvedDocs = allDocs.filter((d) => d.verified === true && d.rejected !== true)
    const rejectedDocs = allDocs.filter((d) => d.rejected === true)
    const pendingDocs  = allDocs.filter((d) => d.verified === false && d.rejected !== true)

    // ── Avg days (approved only) ──
    let totalDays = 0, countForAvg = 0
    kycApprovedList.forEach((m) => {
      const docs      = m.kycDetails || []
      const verifiedTs = docs.filter((d) => d.verifiedDate).map((d) => new Date(d.verifiedDate).getTime())
      const uploadTs   = docs.filter((d) => d.uploadDate).map((d) => new Date(d.uploadDate).getTime())
      if (verifiedTs.length && uploadTs.length) {
        const days = (Math.max(...verifiedTs) - Math.min(...uploadTs)) / 86400000
        if (days >= 0 && days < 365) { totalDays += days; countForAvg++ }
      }
    })
    const avgDaysToApproval = countForAvg > 0 ? (totalDays / countForAvg).toFixed(1) : null

    // ── Enriched: awaiting review (with waiting time) ──
    const kycUploadedEnriched = kycUploadedList
      .map((m) => {
        const uploadTs = (m.kycDetails || []).filter((d) => d.uploadDate).map((d) => new Date(d.uploadDate).getTime())
        const earliest = uploadTs.length ? Math.min(...uploadTs) : null
        return {
          ...m,
          daysWaiting:   earliest ? Math.floor((now - earliest) / 86400000) : null,
          submittedDate: earliest ? new Date(earliest).toISOString() : null,
        }
      })
      .sort((a, b) => (b.daysWaiting ?? 0) - (a.daysWaiting ?? 0))

    const overdueList = kycUploadedEnriched.filter((m) => (m.daysWaiting ?? 0) > OVERDUE_DAYS)

    // ── Enriched: not started (with days since registration) ──
    const kycPendingEnriched = kycPendingList
      .map((m) => ({
        ...m,
        daysSinceReg: m.createdDate
          ? Math.floor((now - new Date(m.createdDate).getTime()) / 86400000)
          : null,
      }))
      .sort((a, b) => (b.daysSinceReg ?? 0) - (a.daysSinceReg ?? 0))

    // ── Enriched: approved (with approval date + days taken) ──
    const kycApprovedEnriched = kycApprovedList
      .map((m) => {
        const docs       = m.kycDetails || []
        const verifiedTs = docs.filter((d) => d.verifiedDate).map((d) => new Date(d.verifiedDate).getTime())
        const uploadTs   = docs.filter((d) => d.uploadDate).map((d) => new Date(d.uploadDate).getTime())
        let daysToApproval = null
        let approvalDate   = null
        if (verifiedTs.length && uploadTs.length) {
          const days = (Math.max(...verifiedTs) - Math.min(...uploadTs)) / 86400000
          if (days >= 0 && days < 365) daysToApproval = parseFloat(days.toFixed(1))
          approvalDate = new Date(Math.max(...verifiedTs)).toISOString()
        }
        return {...m, daysToApproval, approvalDate}
      })
      .sort((a, b) => (b.daysToApproval ?? 0) - (a.daysToApproval ?? 0))

    // ── Enriched: rejected docs (document-level, with stable _rowId) ──
    const rejectedDocsEnriched = rejectedDocs.map((d, i) => ({
      ...d,
      _rowId: `${d.merchantOid}-${d.kycDocName}-${d.verifiedDate || 'x'}-${i}`,
    }))

    // ── Rejected doc type breakdown (chart) ──
    const rejectedDocCounts = {}
    rejectedDocs.forEach((d) => {
      rejectedDocCounts[d.kycDocName || 'Unknown'] = (rejectedDocCounts[d.kycDocName || 'Unknown'] || 0) + 1
    })
    const rejectedDocChartData = Object.entries(rejectedDocCounts)
      .map(([name, value]) => ({name, value}))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)

    // ── Activity log (with stable _rowId) ──
    const activityLog = allDocs
      .filter((d) => d.verifiedDate)
      .map((d, i) => ({
        ...d,
        _rowId: `${d.merchantOid}-${d.kycDocName}-${d.verifiedDate}-${i}`,
        action: d.rejected === true ? 'REJECTED' : 'APPROVED',
      }))
      .sort((a, b) => new Date(b.verifiedDate) - new Date(a.verifiedDate))

    // ── Approver summary ──
    const approverMap = {}
    activityLog.forEach((d) => {
      if (!d.verifiedBy) return
      if (!approverMap[d.verifiedBy])
        approverMap[d.verifiedBy] = {approver: d.verifiedBy, approved: 0, rejected: 0}
      if (d.rejected === true) approverMap[d.verifiedBy].rejected++
      else approverMap[d.verifiedBy].approved++
    })
    const approverList = Object.values(approverMap)
      .map((a) => ({...a, total: a.approved + a.rejected}))
      .sort((a, b) => b.total - a.total)

    // ── Rates ──
    const withDocs         = kycApprovedList.length + kycRejectedList.length
    const approvalRate     = withDocs > 0 ? Math.round((kycApprovedList.length / withDocs) * 100) : 0
    const totalActioned    = approvedDocs.length + rejectedDocs.length
    const docRejectionRate = totalActioned > 0 ? Math.round((rejectedDocs.length / totalActioned) * 100) : 0

    // ── Pie chart ──
    const pieData = [
      {name: 'Approved & Live', value: kycApprovedList.length, fill: '#15803d'},
      {name: 'Under Review',    value: kycUploadedList.length, fill: '#2563eb'},
      {name: 'Not Started',     value: kycPendingList.length,  fill: '#b45309'},
      {name: 'Docs Rejected',   value: kycRejectedList.length, fill: '#b91c1c'},
    ].filter((d) => d.value > 0)

    // ── Month-on-Month merchant trend ──
    const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    const momMap = {}
    active.forEach((m) => {
      if (!m.createdDate) return
      const dt = new Date(m.createdDate)
      if (isNaN(dt.getTime())) return
      const sortKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
      const label   = `${MONTH_NAMES[dt.getMonth()]} '${String(dt.getFullYear()).slice(2)}`
      if (!momMap[sortKey])
        momMap[sortKey] = {month: label, _sortKey: sortKey, approved: 0, notStarted: 0, awaitingReview: 0, docsRejected: 0}
      if      (m.status === 'KYC_APPROVED') momMap[sortKey].approved++
      else if (m.status === 'KYC_UPLOADED') momMap[sortKey].awaitingReview++
      else if (isMerchantKycRejected(m)) momMap[sortKey].docsRejected++
      else momMap[sortKey].notStarted++
    })
    const momTrend = Object.values(momMap)
      .sort((a, b) => a._sortKey.localeCompare(b._sortKey))
      .slice(-24)
      // eslint-disable-next-line no-unused-vars
      .map(({_sortKey, ...rest}) => rest)

    return {
      total, active,
      kycPendingList, kycPendingEnriched,
      kycUploadedList: kycUploadedEnriched,
      kycApprovedList, kycApprovedEnriched,
      kycRejectedList,
      allDocs, approvedDocs, rejectedDocs, rejectedDocsEnriched, pendingDocs,
      avgDaysToApproval, overdueList, rejectedDocChartData,
      activityLog, approverList, approvalRate, docRejectionRate, pieData, momTrend,
    }
  }, [rawData])

  // ── Column definitions ──────────────────────────────────────────────────────

  // — Tab 0: Awaiting Review —
  const awaitingColumns = useMemo(() => [
    {field: 'merchantId', headerName: 'Merchant ID',   flex: 0.75, align: 'left',   headerAlign: 'left'},
    {
      field: 'name', headerName: 'Merchant Name', flex: 1.3, align: 'left', headerAlign: 'left',
      renderCell: (col) => <MerchantNameCell row={col.row} />,
    },
    {field: 'state', headerName: 'State', flex: 0.7, align: 'left', headerAlign: 'left'},
    {
      field: 'kycDetails', headerName: 'Docs Submitted', flex: 0.8, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '13px', fontWeight: 600}}>{(col.row.kycDetails || []).length}</Typography>
      ),
    },
    {
      field: 'submittedDate', headerName: 'Submitted On', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '12px', color: C.textSub}}>
          {col.row.submittedDate ? formatDate(col.row.submittedDate) : '—'}
        </Typography>
      ),
    },
    {
      field: 'daysWaiting', headerName: 'Waiting', flex: 0.7, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const d = col.row.daysWaiting
        if (d == null) return <Typography sx={{fontSize: '12px', color: C.textMuted}}>—</Typography>
        const [bg, color, borderColor] =
          d > 14 ? [C.redBg,   C.red,   C.redBorder]   :
          d > 7  ? [C.amberBg, C.amber, C.amberBorder] :
                   [C.greenBg, C.green, C.greenBorder]
        return (
          <Box sx={{px: 1.2, py: 0.3, borderRadius: '6px', backgroundColor: bg, border: `1px solid ${borderColor}`}}>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color}}>{d}d</Typography>
          </Box>
        )
      },
    },
  ], [])

  // — Tab 1: Rejected KYCs —
  const rejectedColumns = useMemo(() => [
    {field: 'merchantId', headerName: 'Merchant ID',   flex: 1.1, align: 'left', headerAlign: 'left'},
    {
      field: 'name', headerName: 'Merchant Name', flex: 1.2, align: 'left', headerAlign: 'left',
      renderCell: (col) => <MerchantNameCell row={col.row} />,
    },
    {field: 'state', headerName: 'State', flex: 0.9, align: 'left', headerAlign: 'left'},
    {
      field: 'rejectedDocs', headerName: 'Rejected Documents', flex: 2, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const rejected = (col.row.kycDetails || []).filter((d) => d.rejected === true)
        if (!rejected.length)
          return <Typography sx={{fontSize: '12px', color: C.textMuted}}>—</Typography>
        return (
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5}}>
            {rejected.map((d, i) => (
              <Chip key={i} icon={<HighlightOffIcon sx={{fontSize: '13px !important'}} />}
                label={d.kycDocName} size='small' variant='outlined'
                sx={{fontSize: '10px', height: 20, borderColor: C.redBorder, color: C.red,
                  '& .MuiChip-icon': {color: C.red}}} />
            ))}
          </Box>
        )
      },
    },
    {
      field: 'rejectedBy', headerName: 'Rejected By', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const names = [...new Set(
          (col.row.kycDetails || [])
            .filter((d) => d.rejected)
            .map((d) => d.verifiedBy || d.rejectedBy)
            .filter(Boolean)
        )]
        return <Typography sx={{fontSize: '12px', color: C.textSub}}>{names.join(', ') || '—'}</Typography>
      },
    },
    {
      field: 'rejectedAt', headerName: 'Rejected At', flex: 1.2, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const dates = (col.row.kycDetails || [])
          .filter((d) => d.rejected && (d.verifiedDate || d.rejectedDate))
          .map((d) => d.verifiedDate || d.rejectedDate)
          .sort()
          .reverse()
        return (
          <Typography sx={{fontSize: '12px', color: C.textSub}}>
            {dates.length ? formatDate(dates[0]) : '—'}
          </Typography>
        )
      },
    },
  ], [])

  // — Tab 2: Overdue —
  const overdueColumns = useMemo(() => [
    {field: 'merchantId', headerName: 'Merchant ID',   flex: 0.75, align: 'left', headerAlign: 'left'},
    {
      field: 'name', headerName: 'Merchant Name', flex: 1.3, align: 'left', headerAlign: 'left',
      renderCell: (col) => <MerchantNameCell row={col.row} />,
    },
    {field: 'state', headerName: 'State', flex: 0.7, align: 'left', headerAlign: 'left'},
    {
      field: 'submittedDate', headerName: 'Submitted On', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '12px', color: C.textSub}}>
          {col.row.submittedDate ? formatDate(col.row.submittedDate) : '—'}
        </Typography>
      ),
    },
    {
      field: 'daysWaiting', headerName: 'Days Overdue', flex: 0.9, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const d = col.row.daysWaiting
        const [color] = d > 30 ? [C.red] : d > 14 ? [C.amber] : [C.sky]
        return (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.6}}>
            <WarningAmberIcon sx={{fontSize: 14, color}} />
            <Typography sx={{fontSize: '13px', fontWeight: 700, color}}>{d}d</Typography>
          </Box>
        )
      },
    },
    {
      field: 'docCount', headerName: 'Docs Pending', flex: 0.8, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const n = (col.row.kycDetails || []).filter((d) => !d.verified && !d.rejected).length
        return <Typography sx={{fontSize: '13px', fontWeight: 600}}>{n}</Typography>
      },
    },
  ], [])


  // — Drawer: All Merchants —
  const allMerchantsColumns = useMemo(() => [
    {field: 'merchantId', headerName: 'Merchant ID', flex: 0.8, align: 'left', headerAlign: 'left'},
    {
      field: 'name', headerName: 'Merchant Name', flex: 1.3, align: 'left', headerAlign: 'left',
      renderCell: (col) => <MerchantNameCell row={col.row} />,
    },
    {
      field: 'status', headerName: 'KYC Status', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => <StatusChip status={col.row.status} />,
    },
    {field: 'state', headerName: 'State', flex: 0.65, align: 'left', headerAlign: 'left'},
    {
      field: 'createdDate', headerName: 'Registered On', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '12px', color: C.textSub}}>
          {col.row.createdDate ? formatDate(col.row.createdDate) : '—'}
        </Typography>
      ),
    },
  ], [])

  // — Drawer: KYC Not Started —
  const notStartedColumns = useMemo(() => [
    {field: 'merchantId', headerName: 'Merchant ID', flex: 0.8, align: 'left', headerAlign: 'left'},
    {
      field: 'name', headerName: 'Merchant Name', flex: 1.3, align: 'left', headerAlign: 'left',
      renderCell: (col) => <MerchantNameCell row={col.row} />,
    },
    {field: 'state', headerName: 'State', flex: 0.65, align: 'left', headerAlign: 'left'},
    {
      field: 'createdDate', headerName: 'Registered On', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '12px', color: C.textSub}}>
          {col.row.createdDate ? formatDate(col.row.createdDate) : '—'}
        </Typography>
      ),
    },
    {
      field: 'daysSinceReg', headerName: 'Days Pending', flex: 0.85, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const d = col.row.daysSinceReg
        if (d == null) return <Typography sx={{fontSize: '12px', color: C.textMuted}}>—</Typography>
        const [bg, color, border] =
          d > 30 ? [C.redBg,   C.red,   C.redBorder]   :
          d > 14 ? [C.amberBg, C.amber, C.amberBorder] :
                   [C.skyBg,   C.sky,   C.skyBorder]
        return (
          <Box sx={{px: 1.2, py: 0.3, borderRadius: '6px', backgroundColor: bg, border: `1px solid ${border}`}}>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color}}>{d}d</Typography>
          </Box>
        )
      },
    },
  ], [])

  // — Drawer: Approved & Live / Approval Rate / Avg Days —
  const approvedColumns = useMemo(() => [
    {field: 'merchantId', headerName: 'Merchant ID', flex: 0.8, align: 'left', headerAlign: 'left'},
    {
      field: 'name', headerName: 'Merchant Name', flex: 1.3, align: 'left', headerAlign: 'left',
      renderCell: (col) => <MerchantNameCell row={col.row} />,
    },
    {field: 'state', headerName: 'State', flex: 0.65, align: 'left', headerAlign: 'left'},
    {
      field: 'approvalDate', headerName: 'Approved On', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '12px', color: C.textSub}}>
          {col.row.approvalDate ? formatDate(col.row.approvalDate) : '—'}
        </Typography>
      ),
    },
    {
      field: 'daysToApproval', headerName: 'Days Taken', flex: 0.85, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const d = col.row.daysToApproval
        if (d == null) return <Typography sx={{fontSize: '12px', color: C.textMuted}}>—</Typography>
        const [bg, color, border] =
          d <= 3 ? [C.greenBg, C.green, C.greenBorder] :
          d <= 7 ? [C.amberBg, C.amber, C.amberBorder] :
                   [C.redBg,   C.red,   C.redBorder]
        return (
          <Box sx={{px: 1.2, py: 0.3, borderRadius: '6px', backgroundColor: bg, border: `1px solid ${border}`}}>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color}}>{d}d</Typography>
          </Box>
        )
      },
    },
  ], [])

  // — Drawer: Doc Rejection Rate (document-level) —
  const rejectedDocsColumns = useMemo(() => [
    {field: 'merchantId', headerName: 'Merchant ID', flex: 0.8, align: 'left', headerAlign: 'left'},
    {
      field: 'merchantName', headerName: 'Merchant', flex: 1.2, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Link to={`/merchant-config/merchant/${col.row.merchantOid}`}
          style={{color: C.blue, textDecoration: 'none', fontWeight: 600}}>
          {col.row.merchantName}
        </Link>
      ),
    },
    {
      field: 'kycDocName', headerName: 'Document Type', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Box sx={{px: 1.1, py: 0.25, borderRadius: '5px', backgroundColor: C.redBg,
          border: `1px solid ${C.redBorder}`, display: 'inline-flex'}}>
          <Typography sx={{fontSize: '10.5px', fontWeight: 600, color: C.red}}>{col.row.kycDocName}</Typography>
        </Box>
      ),
    },
    {
      field: 'verifiedBy', headerName: 'Rejected By', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '12px', color: C.textSub}}>{col.row.verifiedBy || '—'}</Typography>
      ),
    },
    {
      field: 'verifiedDate', headerName: 'Rejected On', flex: 1, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '12px', color: C.textSub}}>
          {col.row.verifiedDate ? formatDate(col.row.verifiedDate) : '—'}
        </Typography>
      ),
    },
  ], [])

  // — Approver Performance table —
  const approverColumns = useMemo(() => [
    {
      field: 'approver', headerName: 'Approver', flex: 1.6, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const initials = col.row.approver
          .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('')
        return (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '8px', backgroundColor: C.blueBg,
              border: `1px solid ${C.blueBorder}`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0, color: C.blue, fontWeight: 800, fontSize: '0.72rem',
            }}>
              {initials}
            </Box>
            <Typography sx={{fontSize: '0.8rem', fontWeight: 600, color: C.text}} title={col.row.approver}>
              {col.row.approver}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'total', headerName: 'Docs Reviewed', flex: 0.85, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: C.text}}>{col.row.total}</Typography>
      ),
    },
    {
      field: 'approved', headerName: 'Docs Approved', flex: 0.85, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        <Box sx={{px: 1.25, py: 0.3, borderRadius: '6px', backgroundColor: C.greenBg,
          border: `1px solid ${C.greenBorder}`, display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
          <Typography sx={{fontSize: '0.78rem', fontWeight: 700, color: C.green}}>{col.row.approved}</Typography>
        </Box>
      ),
    },
    {
      field: 'rejected', headerName: 'Docs Rejected', flex: 0.85, align: 'left', headerAlign: 'left',
      renderCell: (col) => (
        col.row.rejected > 0
          ? <Box sx={{px: 1.25, py: 0.3, borderRadius: '6px', backgroundColor: C.redBg,
              border: `1px solid ${C.redBorder}`, display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
              <Typography sx={{fontSize: '0.78rem', fontWeight: 700, color: C.red}}>{col.row.rejected}</Typography>
            </Box>
          : <Typography sx={{fontSize: '0.82rem', color: C.textMuted}}>—</Typography>
      ),
    },
    {
      field: 'approvalRate', headerName: 'Approval Rate', flex: 1.2, align: 'left', headerAlign: 'left',
      renderCell: (col) => {
        const rate = col.row.total > 0 ? Math.round((col.row.approved / col.row.total) * 100) : 0
        const barColor = rate >= 90 ? C.green : rate >= 70 ? C.amber : C.red
        return (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', pr: 1}}>
            <Box sx={{flex: 1, height: 6, borderRadius: '999px', backgroundColor: C.border, overflow: 'hidden'}}>
              <Box sx={{height: '100%', width: `${rate}%`, borderRadius: '999px', backgroundColor: barColor}} />
            </Box>
            <Typography sx={{fontSize: '0.78rem', fontWeight: 700, color: barColor, minWidth: 36, textAlign: 'right'}}>
              {rate}%
            </Typography>
          </Box>
        )
      },
    },
  ], [])

  // ── Empty state ─────────────────────────────────────────────────────────────
  const EmptyState = ({icon, text}) => (
    <Box sx={{py: 7, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5}}>
      <Box sx={{width: 56, height: 56, borderRadius: '14px', backgroundColor: C.surface,
        border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        {React.cloneElement(icon, {sx: {fontSize: 26, color: C.textMuted}})}
      </Box>
      <Typography sx={{fontSize: '0.85rem', color: C.textMuted, fontWeight: 500}}>{text}</Typography>
    </Box>
  )

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading && !rawData.length) {
    // Reusable skeleton helpers (inline — skeleton-only render path)
    const SkSection = ({w = 140, children}) => (
      <Box sx={{borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden', bgcolor: C.card}}>
        <Box sx={{px: 2.5, py: 1.5, borderBottom: `1px solid ${C.border}`, bgcolor: C.surface}}>
          <Skeleton variant='rounded' width={w} height={14} />
        </Box>
        <Box sx={{p: 2.5}}>{children}</Box>
      </Box>
    )
    const SkKpi = () => (
      <Box sx={{borderRadius: '14px', border: `1px solid ${C.border}`, bgcolor: C.card, p: '18px 20px 16px'}}>
        <Skeleton variant='rounded' width={38} height={38} sx={{borderRadius: '10px', mb: 1.5}} />
        <Skeleton variant='rounded' width={72} height={28} sx={{mb: 0.75}} />
        <Skeleton variant='rounded' width={110} height={11} sx={{mb: 0.5}} />
        <Skeleton variant='rounded' width={140} height={11} />
      </Box>
    )
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>

        {/* Title */}
        <Skeleton variant='rounded' width={160} height={26} />

        {/* Onboarding Funnel — 5 cards */}
        <SkSection w={150}>
          <Grid container spacing={2}>
            {Array.from({length: 5}).map((_, i) => (
              <Grid item xs={12} sm={6} md={2.4} key={i}><SkKpi /></Grid>
            ))}
          </Grid>
        </SkSection>

        {/* Performance KPIs — 4 cards */}
        <SkSection w={140}>
          <Grid container spacing={2}>
            {Array.from({length: 4}).map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}><SkKpi /></Grid>
            ))}
          </Grid>
        </SkSection>

        {/* Charts row */}
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <SkSection w={120}>
              <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                <Skeleton variant='circular' width={190} height={190} />
              </Box>
            </SkSection>
          </Grid>
          <Grid item xs={12} md={8}>
            <SkSection w={250}>
              <Skeleton variant='rounded' width='100%' height={252} sx={{borderRadius: '8px'}} />
            </SkSection>
          </Grid>
        </Grid>

        {/* Pipeline depth */}
        <SkSection w={185}>
          <Skeleton variant='rounded' width='100%' height={32} sx={{borderRadius: '10px', mb: 2}} />
          <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap'}}>
            {[90, 80, 80, 90].map((w, i) => <Skeleton key={i} variant='rounded' width={w} height={12} />)}
          </Box>
        </SkSection>

        {/* MoM trend */}
        <SkSection w={220}>
          <Skeleton variant='rounded' width='100%' height={316} sx={{borderRadius: '8px'}} />
        </SkSection>

      </Box>
    )
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>

      {/* ── Page header ── */}
      <PageTitle>KYC Analytics</PageTitle>

      {/* ── ONBOARDING FUNNEL ── */}
      <SectionCard title='Onboarding Funnel'>
        <Grid container spacing={2}>
          {[
            {
              icon: <PeopleOutlineIcon />, label: 'Total Merchants', value: M?.total,
              color: C.blue, bg: C.blueBg, border: C.blueBorder,
              sub: 'All active accounts', tooltipProps: TIPS.totalMerchants,
            },
            {
              icon: <HourglassEmptyIcon />, label: 'KYC Not Started', value: M?.kycPendingList?.length,
              color: C.amber, bg: C.amberBg, border: C.amberBorder,
              sub: 'Docs not yet uploaded', tooltipProps: TIPS.kycNotStarted,
              highlight: !!(M?.kycPendingList?.length),
            },
            {
              icon: <PendingActionsIcon />, label: 'Awaiting Review', value: M?.kycUploadedList?.length,
              color: C.sky, bg: C.skyBg, border: C.skyBorder,
              sub: 'Submitted, pending approval', tooltipProps: TIPS.awaitingReview,
              highlight: !!(M?.kycUploadedList?.length),
            },
            {
              icon: <CheckCircleOutlineIcon />, label: 'Approved & Live', value: M?.kycApprovedList?.length,
              color: C.green, bg: C.greenBg, border: C.greenBorder,
              sub: 'Fully verified & active', tooltipProps: TIPS.approvedLive,
            },
            {
              icon: <HighlightOffIcon />, label: 'Docs Rejected', value: M?.kycRejectedList?.length,
              color: C.red, bg: C.redBg, border: C.redBorder,
              sub: 'At least one doc rejected', tooltipProps: TIPS.docsRejected,
              highlight: !!(M?.kycRejectedList?.length),
            },
          ].map((card, i) => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <KpiCard {...card} />
            </Grid>
          ))}
        </Grid>
      </SectionCard>

      {/* ── PERFORMANCE KPIs ── */}
      <SectionCard title='Performance KPIs'>
        <Grid container spacing={2}>
          {[
            {
              icon: <SpeedIcon />,
              label: 'Approval Rate',
              value: M ? `${M.approvalRate}%` : '—',
              sub: 'Merchants approved vs submitted',
              tooltipProps: TIPS.approvalRate,
              ...(M?.approvalRate >= 85
                ? {color: C.green, bg: C.greenBg, border: C.greenBorder}
                : M?.approvalRate >= 65
                  ? {color: C.amber, bg: C.amberBg, border: C.amberBorder}
                  : {color: C.red, bg: C.redBg, border: C.redBorder}),
              highlight: true,
            },
            {
              icon: <AccessTimeIcon />,
              label: 'Avg. Days to Approval',
              value: M?.avgDaysToApproval != null ? `${M.avgDaysToApproval}d` : '—',
              sub: 'First upload → last doc approved',
              color: C.purple, bg: C.purpleBg, border: C.purpleBorder,
              tooltipProps: TIPS.avgDays,
            },
            {
              icon: <WarningAmberIcon />,
              label: 'Overdue Reviews',
              value: M?.overdueList?.length ?? '—',
              sub: 'Waiting for review > 7 days',
              tooltipProps: TIPS.overdue,
              ...(M?.overdueList?.length > 0
                ? {color: C.amber, bg: C.amberBg, border: C.amberBorder, highlight: true}
                : {color: C.green, bg: C.greenBg, border: C.greenBorder}),
            },
            {
              icon: <TrendingDownIcon />,
              label: 'Doc Rejection Rate',
              value: M ? `${M.docRejectionRate}%` : '—',
              sub: 'Of all reviewed documents',
              tooltipProps: TIPS.docRejectionRate,
              ...(M?.docRejectionRate <= 10
                ? {color: C.green, bg: C.greenBg, border: C.greenBorder}
                : M?.docRejectionRate <= 25
                  ? {color: C.amber, bg: C.amberBg, border: C.amberBorder, highlight: true}
                  : {color: C.red, bg: C.redBg, border: C.redBorder, highlight: true}),
            },
          ].map((card, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <KpiCard {...card} />
            </Grid>
          ))}
        </Grid>
      </SectionCard>

      {/* ── CHARTS ROW ── */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <SectionCard title='KYC Status Mix'>
            <Box sx={{display: 'flex', justifyContent: 'center', py: 0.5}}>
              {M?.pieData?.length ? (
                <PieChart data={M.pieData} height={260} innerRadius={68} outerRadius={106} />
              ) : (
                <Box sx={{py: 6, display: 'flex', justifyContent: 'center'}}><CircularProgress size={28} /></Box>
              )}
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <SectionCard
            title='Most Frequently Rejected Documents'
            badge={M?.rejectedDocChartData?.length || undefined}
            right={
              M?.rejectedDocChartData?.length ? (
                <Tooltip
                  title={<MetricTooltip
                    description='Counts how many times each document type has been rejected across all merchants. Helps identify systemic issues with specific document categories.'
                    tip='High counts for a single doc type may mean merchants need clearer upload instructions for that document.'
                  />}
                  placement='top'
                  componentsProps={{tooltip: {sx: {bgcolor: '#1e293b', '& .MuiTooltip-arrow': {color: '#1e293b'}, borderRadius: '10px', p: 0}}}}
                  arrow
                >
                  <IconButton size='small' sx={{color: C.textMuted, borderRadius: '6px', '&:hover': {color: C.textSub}}}>
                    <InfoOutlinedIcon sx={{fontSize: 14}} />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          >
            {M?.rejectedDocChartData?.length ? (
              <VerticalBarChart data={M.rejectedDocChartData} xKey='name' yKey='value' height={250} color={C.red} />
            ) : (
              <Box sx={{py: 5, textAlign: 'center'}}>
                <Typography sx={{fontSize: '0.82rem', color: C.textMuted}}>
                  {M ? '🎉 No rejected documents found' : 'Loading…'}
                </Typography>
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* ── PIPELINE DEPTH ── */}
      <SectionCard
        title='Onboarding Pipeline Depth'
        right={
          <Tooltip
            title={<MetricTooltip
              description='Proportional breakdown of all merchants by their current KYC status. Shows where merchants are in the onboarding funnel at a glance.'
              tip='A healthy pipeline has a large "Approved" segment. A large "Not Started" segment indicates an onboarding drop-off problem.'
            />}
            placement='top'
            componentsProps={{tooltip: {sx: {bgcolor: '#1e293b', '& .MuiTooltip-arrow': {color: '#1e293b'}, borderRadius: '10px', p: 0}}}}
            arrow
          >
            <IconButton size='small' sx={{color: C.textMuted, borderRadius: '6px', '&:hover': {color: C.textSub}}}>
              <InfoOutlinedIcon sx={{fontSize: 14}} />
            </IconButton>
          </Tooltip>
        }
      >
        {M ? (
          <FunnelBar
            segments={[
              {label: 'Approved & Live', value: M.kycApprovedList.length,  color: '#15803d'},
              {label: 'Under Review',    value: M.kycUploadedList.length,   color: '#2563eb'},
              {label: 'Not Started',     value: M.kycPendingList.length,    color: '#b45309'},
              {label: 'Docs Rejected',   value: M.kycRejectedList.length,   color: '#b91c1c'},
            ]}
          />
        ) : (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}><CircularProgress size={24} /></Box>
        )}
      </SectionCard>

      {/* ── MONTH-ON-MONTH TREND ── */}
      {M?.momTrend?.length > 0 && (
        <SectionCard
          title='Month-on-Month Merchant Trend'
          right={
            <Tooltip
              title={<MetricTooltip
                description='Total merchants registered each month, split by their current KYC status. Helps identify seasonal onboarding spikes and track how KYC completion patterns change over time.'
                tip='Use this to spot months with high registrations but low approvals — a signal of onboarding friction.'
              />}
              placement='top'
              componentsProps={{tooltip: {sx: {bgcolor: '#1e293b', '& .MuiTooltip-arrow': {color: '#1e293b'}, borderRadius: '10px', p: 0}}}}
              arrow
            >
              <IconButton size='small' sx={{color: C.textMuted, borderRadius: '6px', '&:hover': {color: C.textSub}}}>
                <InfoOutlinedIcon sx={{fontSize: 14}} />
              </IconButton>
            </Tooltip>
          }
        >
          <BarChart
            data={M.momTrend}
            xKey='month'
            height={300}
            chartOptions={{
              xaxis: 'month',
              data: [
                {dataKey: 'approved',       label: 'Approved & Live',  fill: '#15803d'},
                {dataKey: 'notStarted',     label: 'KYC Not Started',  fill: '#b45309'},
                {dataKey: 'awaitingReview', label: 'Awaiting Review',  fill: '#0369a1'},
                {dataKey: 'docsRejected',   label: 'Docs Rejected',    fill: '#b91c1c'},
              ],
            }}
          />
        </SectionCard>
      )}

      {/* ── APPROVER PERFORMANCE ── */}
      {M?.approverList?.length > 0 && (
        <SectionCard
          title='Approver Performance'
          badge={M.approverList.length}
          right={
            <Tooltip
              arrow
              placement='top'
              title='Counts are at the KYC document level, not merchant level. Each merchant submits multiple documents (PAN, GST, bank details, etc.), so the total docs reviewed will exceed the total merchant count.'
              componentsProps={{tooltip: {sx: {fontSize: '0.72rem', maxWidth: 280}}}}
            >
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.6, cursor: 'default'}}>
                <InfoOutlinedIcon sx={{fontSize: 13, color: C.textMuted}} />
                <Typography sx={{fontSize: '0.7rem', color: C.textMuted}}>
                  {M.approvedDocs.length + M.rejectedDocs.length} docs reviewed across {M.approverList.length} approvers · document-level counts
                </Typography>
              </Box>
            </Tooltip>
          }
        >
          <ProzoDataGrid
            columns={approverColumns}
            getRowId={(r) => r.approver}
            rows={M.approverList}
            autoHeight
            hideFooterPagination={false}
            sizePerPage={approverSize}
            setSizePerPage={setApproverSize}
            setPage={setApproverPage}
            page={approverPage}
            pagination
            paginationMode='client'
            rowCount={M.approverList.length}
            flat={true}
          />
        </SectionCard>
      )}


      {/* ── MERCHANT DETAILS DRAWER ── */}
      <Drawer
        anchor='right'
        open={drawerState.open}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: {xs: '100%', sm: '88%', md: '74%'},
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: C.surface,
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${C.border}`,
            backgroundColor: C.card,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={{fontWeight: 700, fontSize: '1rem', color: C.text, lineHeight: 1.3}}>
              {drawerState.title}
            </Typography>
            {drawerState.subtitle && (
              <Typography sx={{fontSize: '0.74rem', color: C.textMuted, mt: 0.3}}>
                {drawerState.subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            {drawerState.rows.length > 0 && (
              <Box sx={{px: 1.25, py: 0.3, borderRadius: '6px', backgroundColor: C.blueBg,
                border: `1px solid ${C.blueBorder}`}}>
                <Typography sx={{fontSize: '0.72rem', fontWeight: 700, color: C.blue}}>
                  {drawerState.rows.length} records
                </Typography>
              </Box>
            )}
            <IconButton
              onClick={closeDrawer}
              size='small'
              sx={{
                color: C.textMuted,
                borderRadius: '8px',
                '&:hover': {color: C.text, backgroundColor: C.divider},
              }}
            >
              <CloseIcon sx={{fontSize: 18}} />
            </IconButton>
          </Box>
        </Box>

        {/* Drawer body */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: C.card,
            // ── Compact + professional overrides ──
            // NOTE: Do NOT set height/minHeight/maxHeight on columnHeaders or columnHeader —
            // those fight ProzoDataGrid's own `maxHeight: none !important` / `height: auto !important`
            // rules, corrupt MUI's internal size calculation, and cause the last row to be clipped.
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: C.surface,
              borderBottom: `1px solid ${C.border}`,
            },
            '& .MuiDataGrid-columnHeader': {
              pl: '14px !important',
              pr: '14px !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.68rem !important',
              fontWeight: '700 !important',
              color: `${C.textSub} !important`,
              letterSpacing: '0.06em !important',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-columnSeparator': {display: 'none'},
            // Force left-align on every column header and cell
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start !important',
            },
            '& .MuiDataGrid-cell': {
              justifyContent: 'flex-start !important',
              pl: '14px !important',
              pr: '14px !important',
              fontSize: '0.8rem',
              borderBottom: `1px solid ${C.divider} !important`,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: C.surface,
            },
            '& .prozo-stripe': {
              backgroundColor: `${C.surface} !important`,
            },
            '& .prozo-stripe:hover': {
              backgroundColor: `${C.divider} !important`,
            },
            '& .MuiDataGrid-footerContainer': {
              minHeight: '46px',
              borderTop: `1px solid ${C.border}`,
              backgroundColor: C.surface,
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.75rem',
            },
          }}
        >
          {drawerState.rows.length > 0 ? (
            <ProzoDataGrid
              columns={drawerState.columns}
              getRowId={drawerState.getRowId}
              rows={drawerState.rows}
              autoHeight
              hideFooterPagination={false}
              sizePerPage={drawerSize}
              setSizePerPage={setDrawerSize}
              setPage={setDrawerPage}
              page={drawerPage}
              pagination
              paginationMode='client'
              rowCount={drawerState.rows.length}
              flat={true}
              rowHeight={44}
              getRowHeight={drawerState.autoRowHeight ? undefined : () => null}
            />
          ) : (
            <Box sx={{py: 10, textAlign: 'center'}}>
              <Typography sx={{fontSize: '0.85rem', color: C.textMuted}}>No records to display</Typography>
            </Box>
          )}
        </Box>
      </Drawer>

    </Box>
  )
}
